import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken, getUser } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('leads');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [newActivity, setNewActivity] = useState({ activity_type: 'note', title: '', description: '' });
  const [newReminder, setNewReminder] = useState({ reminder_date: '', note: '' });
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  const user = getUser();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchLeads();
    fetchReminders();
    fetchAnalytics();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, statusFilter, searchTerm]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/crm/leads`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setLeads(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  const fetchActivities = async (leadId) => {
    try {
      const response = await axios.get(`${API_URL}/api/crm/leads/${leadId}/activities`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/crm/reminders`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setReminders(response.data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/crm/analytics`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const filterLeads = () => {
    let filtered = leads;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(lead => 
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.phone && lead.phone.includes(searchTerm))
      );
    }
    
    setFilteredLeads(filtered);
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    fetchActivities(lead.id);
    setAiSuggestion(null);
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/crm/leads/${leadId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      fetchLeads();
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
        fetchActivities(leadId);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;

    try {
      await axios.post(
        `${API_URL}/api/crm/leads/${selectedLead.id}/activities`,
        { ...newActivity, lead_id: selectedLead.id },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      setNewActivity({ activity_type: 'note', title: '', description: '' });
      fetchActivities(selectedLead.id);
    } catch (error) {
      console.error('Error adding activity:', error);
      alert('Failed to add activity');
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;

    try {
      await axios.post(
        `${API_URL}/api/crm/reminders`,
        { ...newReminder, lead_id: selectedLead.id },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      setNewReminder({ reminder_date: '', note: '' });
      fetchReminders();
      alert('Reminder created!');
    } catch (error) {
      console.error('Error adding reminder:', error);
      alert('Failed to add reminder');
    }
  };

  const handleAiSuggest = async () => {
    if (!selectedLead || !isAdmin) return;
    
    setLoadingAi(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/crm/leads/${selectedLead.id}/ai-suggest-agent`,
        {},
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      setAiSuggestion(response.data);
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      alert('Failed to get AI suggestion');
    }
    setLoadingAi(false);
  };

  const handleApproveAssignment = async () => {
    if (!selectedLead || !isAdmin) return;

    try {
      await axios.post(
        `${API_URL}/api/crm/leads/${selectedLead.id}/approve-assignment`,
        {},
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      alert('Assignment approved! Agent has been notified.');
      fetchLeads();
      setAiSuggestion(null);
      setSelectedLead(null);
    } catch (error) {
      console.error('Error approving assignment:', error);
      alert('Failed to approve assignment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'Contacted': 'bg-yellow-100 text-yellow-800',
      'Qualified': 'bg-green-100 text-green-800',
      'Negotiating': 'bg-purple-100 text-purple-800',
      'Closed Won': 'bg-green-600 text-white',
      'Closed Lost': 'bg-gray-400 text-white'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="p-8 text-center">Loading CRM...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-[#434345] mb-6">CRM Dashboard</h1>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('leads')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leads'
                  ? 'border-[#BCA480] text-[#BCA480]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Leads ({filteredLeads.length})
            </button>
            <button
              onClick={() => setActiveTab('reminders')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'reminders'
                  ? 'border-[#BCA480] text-[#BCA480]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Reminders ({reminders.length})
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-[#BCA480] text-[#BCA480]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Analytics
            </button>
          </nav>
        </div>

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lead List */}
            <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
              {/* Filters */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="all">All Statuses</option>
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Negotiating">Negotiating</option>
                  <option value="Closed Won">Closed Won</option>
                  <option value="Closed Lost">Closed Lost</option>
                </select>
              </div>

              {/* Lead Items */}
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredLeads.map(lead => (
                  <div
                    key={lead.id}
                    onClick={() => handleLeadClick(lead)}
                    className={`p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                      selectedLead?.id === lead.id ? 'border-[#BCA480] bg-[#FFF9F0]' : 'border-gray-200'
                    }`}
                  >
                    <h3 className="font-semibold text-[#434345]">{lead.name}</h3>
                    <p className="text-sm text-gray-600">{lead.email}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                      <span className="text-xs text-gray-500">{lead.source}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Lead Details */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
              {selectedLead ? (
                <>
                  <div className="mb-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h2 className="text-2xl font-bold text-[#434345]">{selectedLead.name}</h2>
                        <p className="text-gray-600">{selectedLead.email}</p>
                        {selectedLead.phone && <p className="text-gray-600">{selectedLead.phone}</p>}
                      </div>
                      <select
                        value={selectedLead.status}
                        onChange={(e) => handleStatusUpdate(selectedLead.id, e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="New">New</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Qualified">Qualified</option>
                        <option value="Negotiating">Negotiating</option>
                        <option value="Closed Won">Closed Won</option>
                        <option value="Closed Lost">Closed Lost</option>
                      </select>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Property Interest</p>
                        <p className="font-medium">{selectedLead.property_interest || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Budget</p>
                        <p className="font-medium">{selectedLead.budget || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="font-medium">{selectedLead.location_preference || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Lead Score</p>
                        <p className="font-medium">{selectedLead.lead_score}/100</p>
                      </div>
                    </div>

                    {selectedLead.notes && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500">Notes</p>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedLead.notes}</p>
                      </div>
                    )}

                    {/* AI Assignment (Admin Only) */}
                    {isAdmin && !selectedLead.assigned_agent && (
                      <div className="mt-4 p-4 border border-[#BCA480] rounded-lg bg-[#FFF9F0]">
                        <h3 className="font-semibold text-[#434345] mb-2">AI-Powered Assignment</h3>
                        {!aiSuggestion ? (
                          <button
                            onClick={handleAiSuggest}
                            disabled={loadingAi}
                            className="bg-[#BCA480] text-white px-4 py-2 rounded hover:bg-[#a89470] disabled:bg-gray-400"
                          >
                            {loadingAi ? 'Analyzing...' : 'Get AI Suggestion'}
                          </button>
                        ) : (
                          <div>
                            <p className="text-sm text-gray-700 mb-2">
                              <strong>Suggested Agent:</strong> {aiSuggestion.agent_name}
                            </p>
                            <p className="text-sm text-gray-700 mb-2">
                              <strong>Confidence:</strong> {(aiSuggestion.confidence * 100).toFixed(0)}%
                            </p>
                            <p className="text-sm text-gray-600 mb-3">{aiSuggestion.reasoning}</p>
                            <button
                              onClick={handleApproveAssignment}
                              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                            >
                              Approve & Assign
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Activities */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-[#434345] mb-3">Activity Timeline</h3>
                    <form onSubmit={handleAddActivity} className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-1 gap-3">
                        <select
                          value={newActivity.activity_type}
                          onChange={(e) => setNewActivity({ ...newActivity, activity_type: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="note">Note</option>
                          <option value="call">Call</option>
                          <option value="email">Email</option>
                          <option value="meeting">Meeting</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Activity title"
                          value={newActivity.title}
                          onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                        <textarea
                          placeholder="Description (optional)"
                          value={newActivity.description}
                          onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                          rows="2"
                        />
                        <button
                          type="submit"
                          className="bg-[#BCA480] text-white px-4 py-2 rounded hover:bg-[#a89470]"
                        >
                          Add Activity
                        </button>
                      </div>
                    </form>

                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {activities.map(activity => (
                        <div key={activity.id} className="border-l-4 border-[#BCA480] pl-4 py-2">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-[#434345]">{activity.title}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(activity.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <span className="text-xs bg-gray-200 px-2 py-1 rounded">{activity.activity_type}</span>
                          {activity.description && (
                            <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add Reminder */}
                  <div>
                    <h3 className="text-lg font-semibold text-[#434345] mb-3">Set Reminder</h3>
                    <form onSubmit={handleAddReminder} className="p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-1 gap-3">
                        <input
                          type="datetime-local"
                          value={newReminder.reminder_date}
                          onChange={(e) => setNewReminder({ ...newReminder, reminder_date: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                          required
                        />
                        <textarea
                          placeholder="Reminder note"
                          value={newReminder.note}
                          onChange={(e) => setNewReminder({ ...newReminder, note: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                          rows="2"
                          required
                        />
                        <button
                          type="submit"
                          className="bg-[#434345] text-white px-4 py-2 rounded hover:bg-[#2a2a2c]"
                        >
                          Create Reminder
                        </button>
                      </div>
                    </form>
                  </div>
                </>
              ) : (
                <div className="text-center text-gray-500 py-12">
                  Select a lead to view details
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reminders Tab */}
        {activeTab === 'reminders' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-[#434345] mb-4">Upcoming Reminders</h2>
            <div className="space-y-4">
              {reminders.length === 0 ? (
                <p className="text-gray-500">No reminders set</p>
              ) : (
                reminders.map(reminder => (
                  <div key={reminder.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-[#434345]">{reminder.note}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(reminder.reminder_date).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await axios.patch(
                              `${API_URL}/api/crm/reminders/${reminder.id}/complete`,
                              {},
                              { headers: { Authorization: `Bearer ${getAuthToken()}` } }
                            );
                            fetchReminders();
                          } catch (error) {
                            console.error('Error completing reminder:', error);
                          }
                        }}
                        className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        Complete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-[#434345] mb-6">Analytics</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-[#FFF9F0] p-6 rounded-lg border border-[#BCA480]">
                <p className="text-sm text-gray-600">Total Leads</p>
                <p className="text-3xl font-bold text-[#434345]">{analytics.total_leads}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Leads by Status */}
              <div>
                <h3 className="text-lg font-semibold text-[#434345] mb-3">Leads by Status</h3>
                <div className="space-y-2">
                  {Object.entries(analytics.leads_by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <span>{status}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Leads by Source (Admin only) */}
              {isAdmin && analytics.leads_by_source && (
                <div>
                  <h3 className="text-lg font-semibold text-[#434345] mb-3">Leads by Source</h3>
                  <div className="space-y-2">
                    {Object.entries(analytics.leads_by_source).map(([source, count]) => (
                      <div key={source} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                        <span>{source.replace('_', ' ')}</span>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CRMDashboard;
